import { NurseLayout } from "@/components/layout/nurse-layout";
import { Link } from "wouter";
import { ArrowLeft, BookOpenCheck, ExternalLink, CheckCircle } from "lucide-react";

const RCN_BENEFITS = [
  "Access to the RCN Rheumatology Nursing Forum — specialist CPD events, study days, and networking",
  "RCN legal support, employment advice, and professional indemnity for clinical activities",
  "RCN library and research resources — access to journals and systematic reviews",
  "RCN Professional Nursing Committee representation — influencing national nursing policy",
  "RCN Congress — annual conference with clinical and professional development sessions",
  "RCN learning resources: competency frameworks, practice guidance, and clinical toolkits",
];

const BSR_CREDENTIALS = [
  {
    title: "BSR Rheumatology Nursing Credential",
    level: "Specialist",
    desc: "Awarded to nurses who meet the BSR standards for rheumatology specialist nursing practice. Requires portfolio of evidence covering clinical competency, CPD, and professional practice.",
    requirements: ["NMC registered nurse", "Minimum 2 years post-registration experience in rheumatology", "Completion of BSR competency framework", "Portfolio of clinical evidence"],
    url: "https://www.rheumatology.org.uk/",
  },
  {
    title: "EULAR Online Course — Rheumatology for Nurses",
    level: "CPD",
    desc: "EULAR provides structured online learning modules covering inflammatory arthritis, connective tissue disease, and vasculitis, accredited for European CPD.",
    requirements: ["EULAR/BSR member or individual registration", "Online self-study modules + assessed components", "Certificate of completion"],
    url: "https://www.eular.org/",
  },
  {
    title: "RCN Rheumatology Nursing Forum Membership",
    level: "Professional",
    desc: "The RCN Rheumatology Nursing Forum is the specialist nursing forum for UK rheumatology nurses. Membership provides access to CPD events, specialist networks, and the BSR.",
    requirements: ["Active NMC registration", "RCN full membership", "Declared specialist interest in rheumatology"],
    url: "https://www.rcn.org.uk/",
  },
];

const CPD_SOURCES = [
  { org: "BSR (British Society for Rheumatology)", url: "https://www.rheumatology.org.uk/", what: "Annual conference, nurse sessions, webinars, guidelines" },
  { org: "EULAR (European Alliance of Associations for Rheumatology)", url: "https://www.eular.org/", what: "Online courses, annual congress (EULAR), HPR sessions" },
  { org: "RCN Rheumatology Nursing Forum", url: "https://www.rcn.org.uk/", what: "Study days, national meeting, expert speaker events" },
  { org: "BHPR (British Health Professionals in Rheumatology)", url: "https://www.bhpr.org.uk/", what: "Multidisciplinary AHP rheumatology CPD and networking" },
  { org: "ARMA (Arthritis and Musculoskeletal Alliance)", url: "https://www.arma.uk.net/", what: "Policy advocacy, standards, patient-centred care frameworks" },
  { org: "Versus Arthritis", url: "https://www.versusarthritis.org/", what: "Patient education resources, nurse education, helpline training" },
];

export default function RcnSpecialist() {
  return (
    <NurseLayout title="RCN / BSR Specialist Certification">
      <Link href="/nurse/career">
        <button className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-6 transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to Career Hub
        </button>
      </Link>

      <div className="space-y-8">

        <div className="bg-gradient-to-br from-rose-900 to-rose-950 rounded-2xl p-8 text-white">
          <div className="flex items-center gap-4 mb-3">
            <div className="bg-rose-800/60 p-3 rounded-xl">
              <BookOpenCheck className="h-7 w-7 text-rose-300" />
            </div>
            <div>
              <h2 className="text-xl font-bold">RCN / BSR Specialist Certification</h2>
              <p className="text-rose-300 text-sm">Royal College of Nursing · British Society for Rheumatology · EULAR</p>
            </div>
          </div>
          <p className="text-rose-200 text-sm leading-relaxed max-w-3xl">
            Specialist certification through the RCN and BSR formally recognises your expertise as a rheumatology nurse. These credentials support career progression to senior CNS, ANP, and Consultant Nurse roles, and demonstrate commitment to the specialty.
          </p>
        </div>

        {/* BSR/EULAR credentials */}
        <div>
          <h3 className="text-lg font-bold text-slate-900 mb-4">Specialist Credentials & Certifications</h3>
          <div className="space-y-4">
            {BSR_CREDENTIALS.map((cred) => (
              <div key={cred.title} className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-bold text-slate-900">{cred.title}</h4>
                      <span className="text-xs bg-rose-100 text-rose-700 border border-rose-200 px-2 py-0.5 rounded-full font-semibold">{cred.level}</span>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed">{cred.desc}</p>
                  </div>
                  <a href={cred.url} target="_blank" rel="noopener noreferrer" className="shrink-0">
                    <ExternalLink className="h-4 w-4 text-slate-400 hover:text-rose-600 transition-colors" />
                  </a>
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Requirements</p>
                  <ul className="space-y-1">
                    {cred.requirements.map((req, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                        <CheckCircle className="h-4 w-4 text-rose-500 mt-0.5 shrink-0" />
                        {req}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* RCN membership benefits */}
        <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
          <h3 className="font-bold text-rose-900 mb-3 flex items-center gap-2">
            <BookOpenCheck className="h-5 w-5 text-rose-700" />
            RCN Full Membership — What You Get
          </h3>
          <ul className="space-y-2">
            {RCN_BENEFITS.map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-rose-800">
                <CheckCircle className="h-4 w-4 text-rose-500 mt-0.5 shrink-0" />
                {b}
              </li>
            ))}
          </ul>
        </div>

        {/* CPD sources */}
        <div>
          <h3 className="text-lg font-bold text-slate-900 mb-4">Key CPD Sources for Rheumatology Nurses</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {CPD_SOURCES.map((src) => (
              <a key={src.org} href={src.url} target="_blank" rel="noopener noreferrer"
                className="block bg-white border border-slate-200 rounded-xl p-4 hover:border-rose-300 hover:shadow-sm transition-all group">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-semibold text-sm text-slate-900 group-hover:text-rose-700 transition-colors">{src.org}</p>
                  <ExternalLink className="h-3.5 w-3.5 text-slate-300 group-hover:text-rose-500 shrink-0 mt-0.5 transition-colors" />
                </div>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">{src.what}</p>
              </a>
            ))}
          </div>
        </div>
      </div>
    </NurseLayout>
  );
}
